import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import FormCheckbox from "@components/Form/FormCheckbox";
import { Icon } from "@components/Icon";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { FilterContext } from "~/providers/FilterProvider";
import { SearchResultSwatch } from "@ts/search";
import clsx from "clsx";
import React, { useContext } from "react";
import tw from "twin.macro";

const List = styledWithInvalidProps("ul")(
  ({ handle, show }: { handle: string; show: boolean }) => [
    tw`block pb-midi pt-midi list-none transition-all duration-slow`,
    handle === "subcategory" ? tw`grid grid-cols-1` : null,
    show ? tw`max-h-[1500px]` : tw`max-h-[475px]`,
  ],
);

const ListItem = styledWithInvalidProps("li")(({ show }: { show: boolean }) => [
  show ? tw`block` : tw`hidden`,
]);

const Filter = withFocusRing(
  styledWithInvalidProps("button")(
    ({ active, heroBanner }: { active: boolean; heroBanner: boolean }) => [
      tw`relative flex text-left mb-mini text-body mt-micro leading-h1 whitespace-nowrap focus:outline-none items-center`,
      active ? tw`` : tw`hover:underline`,
      active && heroBanner ? tw`underline` : null,
    ],
  ),
);

const StyledIcon = tw(Icon)`
  mr-mini
`;
const Checkbox = withFocusRing(tw(FormCheckbox)`
  absolute inset-0 w-full h-full appearance-none opacity-0 cursor-pointer focus-visible:opacity-100
`);

const Wrapper = tw.div`
  flex w-1/2 mb-midi items-center justify-start transition-presentation hover:text-quaternary
`;

const Button = withFocusRing(tw.button`
   pb-nano border-b text-primary
`);

const FilterProductCount = tw.div`ml-pico text-primary`;

const EmptyFilterLabel = tw.p`
  text-quaternary
`;

const Title = tw.span`
  capitalize
`;

export type FilterSectionListProps = {
  className?: string;
  handle: string;
  handleCategoryClick?: () => void;
  handleChange: (key: string) => void;
  handleViewAllClick?: () => void;
  hideCheckbox?: boolean;
  hideProductCount?: boolean;
  layout?: "heroBanner";
  options: SearchResultSwatch[];
  value: Record<string, any> | string;
  viewAllActive?: boolean;
};

export const FilterSectionList = ({
  className,
  handle,
  handleCategoryClick,
  handleChange,
  handleViewAllClick,
  hideCheckbox,
  hideProductCount,
  layout,
  options,
  value,
  viewAllActive,
}: FilterSectionListProps) => {
  const { showMore, setShowMore, handleScroll } = useContext(FilterContext);
  const handleClick = () => {
    handleScroll();
    if (handleCategoryClick) handleCategoryClick();
  };

  return (
    <>
      <List className={className} handle={handle} show={showMore}>
        {!options.length && layout !== "heroBanner" ? (
          <EmptyFilterLabel>No products found</EmptyFilterLabel>
        ) : null}
        {layout === "heroBanner" ? (
          <li
            className={clsx(
              viewAllActive ? "underline" : "hover:underline",
              "cursor-pointer",
            )}
            onClick={handleViewAllClick}
          >
            View All
          </li>
        ) : null}
        {options?.map(({ key, checked, doc_count }, index) => (
          <ListItem
            key={key.toString()}
            show={index < 10 || layout === "heroBanner" ? true : showMore}
            data-testid="filter-checkbox"
          >
            <Filter
              active={value[key] || value === key}
              heroBanner={layout === "heroBanner"}
              title={key}
              onClick={handleClick}
            >
              <Checkbox
                value={key}
                checked={checked}
                onClick={handleChange || handleClick}
                handleChange={handleChange}
                onKeyPress={handleChange}
                internalState
                tabIndex={-1}
              />
              {!hideCheckbox ? (
                <StyledIcon
                  name={!value[key] ? `tickedBoxInactive` : `tickedBoxActive`}
                  size="xxs"
                />
              ) : null}
              <Title>{key}</Title>
              {!hideProductCount ? (
                <FilterProductCount>{`(${doc_count})`}</FilterProductCount>
              ) : null}
            </Filter>
          </ListItem>
        ))}
      </List>
      {layout !== "heroBanner" && options.length > 10 ? (
        <Wrapper>
          <Button onClick={() => setShowMore(!showMore)}>
            {" "}
            <span>{showMore ? "See less -" : "See more +"}</span>
          </Button>
        </Wrapper>
      ) : null}
    </>
  );
};
